import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Shell } from "./ui/Shell";
import Login from "./routes/Login";
import AcceptInvite from "./routes/AcceptInvite";
import Account from "./routes/Account";
import Sites from "./routes/Sites";
import SiteNew from "./routes/SiteNew";
import SiteDashboard from "./routes/SiteDashboard";
import PagesList from "./routes/PagesList";
import PageEditor from "./routes/PageEditor";
import Theme from "./routes/Theme";
import Variables from "./routes/Variables";
import Members from "./routes/Members";
import Assets from "./routes/Assets";
import Forms from "./routes/Forms";
import FormSubmissions from "./routes/FormSubmissions";
import Builds from "./routes/Builds";
import Sessions from "./routes/Sessions";
import AuditLog from "./routes/AuditLog";
import PageRevisions from "./routes/PageRevisions";
import Redirects from "./routes/Redirects";
import Domains from "./routes/Domains";
import Snippets from "./routes/Snippets";
import SnippetEditor from "./routes/SnippetEditor";
import Collections from "./routes/Collections";
import CollectionNew from "./routes/CollectionNew";
import CollectionDetail from "./routes/CollectionDetail";
import EntryEditor from "./routes/EntryEditor";
import Settings from "./routes/Settings";
import ApiKeys from "./routes/ApiKeys";
import Review from "./routes/Review";
import Webhooks from "./routes/Webhooks";
import RecoveryCodes from "./routes/RecoveryCodes";
import NotFound from "./routes/NotFound";

function Protected({ children }: { children: JSX.Element }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!me?.user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function Index() {
  const { me, loading } = useAuth();
  if (loading) return null;
  return me?.user ? <Navigate to="/sites" replace /> : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route element={<Protected><Shell /></Protected>}>
            <Route path="/sites" element={<Sites />} />
            <Route path="/sites/new" element={<SiteNew />} />
            <Route path="/sites/:slug" element={<SiteDashboard />} />
            <Route path="/sites/:slug/pages" element={<PagesList />} />
            <Route path="/sites/:slug/pages/:page" element={<PageEditor />} />
            <Route path="/sites/:slug/theme" element={<Theme />} />
            <Route path="/sites/:slug/variables" element={<Variables />} />
            <Route path="/sites/:slug/members" element={<Members />} />
            <Route path="/sites/:slug/assets" element={<Assets />} />
            <Route path="/sites/:slug/forms" element={<Forms />} />
            <Route path="/sites/:slug/forms/:block" element={<FormSubmissions />} />
            <Route path="/sites/:slug/builds" element={<Builds />} />
            <Route path="/sites/:slug/audit" element={<AuditLog />} />
            <Route path="/sites/:slug/pages/:page/revisions" element={<PageRevisions />} />
            <Route path="/sites/:slug/redirects" element={<Redirects />} />
            <Route path="/sites/:slug/domains" element={<Domains />} />
            <Route path="/sites/:slug/snippets" element={<Snippets />} />
            <Route path="/sites/:slug/snippets/:id" element={<SnippetEditor />} />
            <Route path="/sites/:slug/collections" element={<Collections />} />
            <Route path="/sites/:slug/collections/new" element={<CollectionNew />} />
            <Route path="/sites/:slug/collections/:col" element={<CollectionDetail />} />
            <Route path="/sites/:slug/collections/:col/entries/:entry" element={<EntryEditor />} />
            <Route path="/sites/:slug/settings" element={<Settings />} />
            <Route path="/sites/:slug/api-keys" element={<ApiKeys />} />
            <Route path="/sites/:slug/review" element={<Review />} />
            <Route path="/sites/:slug/webhooks" element={<Webhooks />} />
            <Route path="/account" element={<Account />} />
            <Route path="/account/sessions" element={<Sessions />} />
            <Route path="/account/recovery-codes" element={<RecoveryCodes />} />
            <Route path="/admin/audit" element={<AuditLog />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
